/*
 * Copyright (c) 2026. Bolmate B.V.
 *
 * Generic multi-step wizard rendered inside a BolmateDialog. The caller
 * declares the steps (title, content, completion rule); the stepper walks the
 * user through them while accumulating one `values` object, and hands the
 * final values to `onFinish` — the caller builds its request from them.
 *
 * Layout per step (matches the design mocks):
 *   header  — step title + "Stap X van Y: <subtitle>" under it
 *   body    — the step's own content
 *   footer  — [Previous step]           [Cancel] [Next / <finishLabel>]
 * Steps that advance from within their content (e.g. picking a card) set
 * `hideNext` so the footer only offers Cancel. An optional `overlay` node
 * (e.g. an are-you-sure interception) renders on top of the whole dialog.
 */
import './style/bolmateStepper.scss';

import React, {ReactNode, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import BolmateDialog from '../dialog/bolmateDialog';

import BolmateButton from '../button/bolmateButton';

export interface BolmateStepperContext<V> {
    values: V;
    /** Shallow-merges the patch into the accumulated values. */
    setValues: (patch: Partial<V>) => void;
    next: () => void;
    back: () => void;
    stepIndex: number;
    isLast: boolean;
}

export interface BolmateStepperStep<V> {
    key: string;
    /** Dialog title while this step is active. */
    title: string;
    /** Rendered as "Stap X van Y: <subtitle>" under the title. */
    subtitle?: string;
    /** Rendered in the dialog header's top-right (left of the close X) while
     *  this step is active — for an action that belongs to the whole step
     *  rather than to the footer. */
    headerAction?: ReactNode;
    render: (context: BolmateStepperContext<V>) => ReactNode;
    /** Gates the footer's Next/Finish button; defaults to always complete. */
    isComplete?: (values: V) => boolean;
    /** Hide the footer's Next/Finish — for steps that advance from within
     *  their content (e.g. clicking a type card). */
    hideNext?: boolean;
    /** Per-step label for the footer's advance button — overrides the
     *  next/finish default (e.g. a mid-wizard "create" action, or "Close" on
     *  a success step). */
    nextLabel?: ReactNode;
    /** Hide "Vorige stap" on this step (e.g. a post-submit success step —
     *  there is nothing to go back to once the action ran). */
    hideBack?: boolean;
    /** Hide "Annuleren" on this step (e.g. a success step whose only exit is
     *  its own advance button). */
    hideCancel?: boolean;
    /** Replaces the default advance behavior for this step. The handler owns
     *  the transition: call context.next() to advance (or close the dialog
     *  itself). Used for steps whose button performs an async action first. */
    onNext?: (context: BolmateStepperContext<V>) => void;
}

interface Props<V> {
    active: boolean;
    onClose: VoidFunction;
    /** Called with ALL accumulated values when the last step is confirmed. */
    onFinish: (values: V) => void;
    steps: Array<BolmateStepperStep<V>>;
    initialValues: V;
    finishLabel: ReactNode;
    nextLabel?: ReactNode;
    backLabel?: ReactNode;
    cancelLabel?: ReactNode;
    /** Disables every footer action (a submit is in flight). */
    loading?: boolean;
    width?: string;
    className?: string;
    /** Rendered on top of the whole dialog (e.g. a confirmation interception).
     *  Footer actions stay disabled while it is shown. */
    overlay?: ReactNode;
}

export default function BolmateStepper<V>(props: Props<V>) {
    const {t} = useTranslation();
    const {active, onClose, onFinish, steps, initialValues, loading, width, className, overlay} = props;
    const [stepIndex, setStepIndex] = useState(0);
    const [values, setValuesState] = useState<V>(initialValues);

    // Every open starts a fresh wizard: first step, pristine values.
    useEffect(() => {
        if (!active) return;
        setStepIndex(0);
        setValuesState(initialValues);
    }, [active]);

    const step = steps[Math.min(stepIndex, steps.length - 1)];
    const isLast = stepIndex >= steps.length - 1;
    const complete = step.isComplete ? step.isComplete(values) : true;

    function setValues(patch: Partial<V>) {
        setValuesState(previous => ({...previous, ...patch}));
    }

    function next() {
        if (isLast) {
            onFinish(values);
            return;
        }
        setStepIndex(index => Math.min(index + 1, steps.length - 1));
    }

    function back() {
        setStepIndex(index => Math.max(index - 1, 0));
    }

    const context: BolmateStepperContext<V> = {values, setValues, next, back, stepIndex, isLast};
    const subtitle = step.subtitle
        ? t('controls.stepper.stepOf', 'Stap {{step}} van {{total}}: {{subtitle}}', {
              step: stepIndex + 1,
              total: steps.length,
              subtitle: step.subtitle
          })
        : undefined;
    const actionsDisabled = loading || !!overlay;

    return (
        <BolmateDialog
            active={active}
            onClose={onClose}
            title={step.title}
            subtitle={subtitle}
            headerAction={step.headerAction}
            className={`bolmate-stepper ${className ?? ''}`}
            width={width}
            hideCancel
            loading={loading}
            // Lock the dialog's own exits (X, backdrop click, Escape) ONLY while
            // a submit is actually in flight — closing then would abandon a
            // create mid-request. The interception overlay (e.g. the duplicate
            // "Notification already exists" warning) must stay dismissable: the
            // user has to be able to back out of it via the X / backdrop /
            // Escape, not only its inline buttons.
            preventBackgroundClick={loading}
            escapeDisabled={loading}
            hideClose={loading}
            overlay={overlay && <div className='stepper-overlay'>{overlay}</div>}
            footer={
                <div className='stepper-footer'>
                    <div className='stepper-footer-left'>
                        {stepIndex > 0 && !step.hideBack && (
                            <BolmateButton
                                tertiary
                                shape='square'
                                label={props.backLabel ?? t('controls.stepper.back', 'Vorige stap')}
                                onClick={back}
                                disabled={actionsDisabled}
                            />
                        )}
                    </div>
                    <div className='stepper-footer-right'>
                        {!step.hideCancel && (
                            <BolmateButton
                                tertiary
                                shape='square'
                                label={props.cancelLabel ?? t('controls.stepper.cancel', 'Annuleren')}
                                onClick={onClose}
                                disabled={actionsDisabled}
                            />
                        )}
                        {!step.hideNext && (
                            <BolmateButton
                                label={
                                    step.nextLabel ??
                                    (isLast
                                        ? props.finishLabel
                                        : (props.nextLabel ?? t('controls.stepper.next', 'Volgende')))
                                }
                                onClick={() => (step.onNext ? step.onNext(context) : next())}
                                disabled={actionsDisabled || !complete}
                            />
                        )}
                    </div>
                </div>
            }
        >
            <div className='stepper-step-content'>{step.render(context)}</div>
        </BolmateDialog>
    );
}
